import { useCurrentUserQuery } from '@/hooks/use-current-user/graphql.generated';
import { ContentSection } from '@/layouts/content-section/component';
import { Accordion } from '@mantine/core';
import { useMemo } from 'react';
import {
	AccordionLocation,
	CouncilsListFilter,
	RoleName,
} from '../../../../backend/src/types.generated';
import { MarkdownComponent } from '../markdown';
import { useAccordionItemsQuery } from './graphql.generated';
import styles from './styles.module.css';

export interface Props {
	title?: string;
	accordionLocation: AccordionLocation;
	additionalItems?: React.ReactNode;
	additionalItemsDefaultValue?: string;
	rootClassName?: string;
}

export const CurbyAccordion = (props: Props) => {
	const { title, additionalItems, additionalItemsDefaultValue, accordionLocation, rootClassName } =
		props;
	const { data: currentUser } = useCurrentUserQuery();
	const council = currentUser?.currentUser?.linkedCouncil?.name;
	const councilFilters: CouncilsListFilter[] = [];
	if (council) councilFilters.push({ name: council });

	const combinedFilter: CouncilsListFilter = { _or: councilFilters };
	const { data } = useAccordionItemsQuery({
		variables: { location: accordionLocation!, council: combinedFilter },
		skip: !accordionLocation,
	});

	const items = useMemo(() => {
		if (!data) return [];
		const councilRecords = council ? (data.council ?? []) : [];
		const allRecords = data.all ?? [];
		const union = councilRecords.concat(allRecords).filter((item) => item !== null);
		const filteredByUserType = union.filter(({ userType }) => {
			const userTypes = currentUser?.currentUser?.roles?.map((role) => role.name) ?? [];
			if (userType === 'ALL') return true;
			else if (userType === 'USER' && userTypes?.includes(RoleName.User)) return true;
			else if (userType === 'ADMIN' && userTypes?.includes(RoleName.Admin)) return true;
			else if (
				userType === 'ALL_ADMINS' &&
				Boolean(userTypes?.includes(RoleName.Admin) && userTypes?.includes(RoleName.SuperAdmin))
			)
				return true;
			else if (userType === 'SUPER_ADMIN' && userTypes?.includes(RoleName.SuperAdmin)) return true;
			return false;
		});
		return filteredByUserType.sort((a, b) => a.itemOrder - b.itemOrder);
	}, [council, data, currentUser]);
	const defaultValue = additionalItemsDefaultValue ?? items.find((item) => item?.isExpanded)?.id;

	if (items.length === 0) {
		return null;
	}

	return (
		<ContentSection title={title} className={rootClassName}>
			<Accordion
				defaultValue={defaultValue}
				variant="contained"
				w="100%"
				className={styles.accordion}
			>
				{additionalItems}
				{items.map((item) => (
					<Accordion.Item key={item?.id} value={item?.id ?? ''} className={styles.accordionItem}>
						<Accordion.Control className={styles.accordionControl}>{item?.title}</Accordion.Control>
						<Accordion.Panel>
							<MarkdownComponent content={item?.content} />
						</Accordion.Panel>
					</Accordion.Item>
				))}
			</Accordion>
		</ContentSection>
	);
};
